import type {
  AuthResponse,
  ChangeEmailPayload,
  ChangePasswordPayload,
  DeleteAccountPayload,
  ForgotPasswordPayload,
  MfaRequiredResponse,
  RefreshTokenPayload,
  ResendOtpPayload,
  ResetPasswordPayload,
  SignInPayload,
  SignUpPayload,
  User,
  UserType,
  VerifyEmailChangePayload,
  VerifyOtpPayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type AuthMutationResponse<T> = T & { message: string };
type RawCurrentUserResponse = {
  user: {
    id: string;
    email: string;
    type: UserType;
    profile?: unknown;
    ownedInstitution?: { id: string } | null;
    institutionMember?: { institutionId: string } | null;
  };
};
type VerifyEmailChangeResponse = AuthMutationResponse<{ user: User }>;

const toUser = (raw: RawCurrentUserResponse['user']): User => ({
  id: raw.id,
  email: raw.email,
  type: raw.type,
  has_profile: !!raw.profile,
  institution_id: raw.ownedInstitution?.id ?? raw.institutionMember?.institutionId,
});

/**
 * Mirrors `apps/server/src/modules/auth` (`authController`, prefix `/auth`) — every network auth
 * route except `POST /auth/refresh-token`, which is intentionally NOT modeled here: it lives in
 * each app's own `edenBaseQuery`/http-client (called reactively on a 401, outside any component
 * render), not as something a UI ever calls directly through RTK Query.
 *
 * There is no separate MFA setup/verify/disable flow server-side — `verify-otp`/`resend-otp` ARE
 * the MFA mechanism, mandatory on every sign-in (`sign-in` always responds with
 * `mfa_required: true` and an email OTP, never a direct session).
 *
 * Every endpoint here is a `builder.mutation`, including the one GET route
 * (`getCurrentUser`) — per instruction, none of them carry RTK Query cache tags. The user/session
 * state itself (who is signed in, the current `User`) stays entirely client-side (Phase 3), so
 * nothing here should become a cached, auto-refetching query that could compete with that
 * client-owned state; call these imperatively and let the app's own auth store hold the result.
 *
 * `message` stripping: checked every response's server-side locale string
 * (`apps/server/src/modules/auth/locales/en.ts`) — none of them interpolate dynamic content
 * (they're plain strings like "Signed in successfully", "A new verification code has been sent"),
 * so per the established i18n-key convention `message` is stripped everywhere **except**
 * `signIn`: its `MfaRequiredResponse` is a pre-existing named type in `packages/shared/src/types`
 * (not a generic `{ entity, message }` envelope invented per-endpoint), already used as-is by
 * `apps/web/src/auth/stores/usecases/sign-in.ts`, and the OTP flow is exactly what was flagged as
 * worth keeping — so it's returned unmodified, message included, rather than narrowing an
 * existing public contract type.
 *
 * `getCurrentUser` (`GET /auth/`) folds in a reshape that both apps currently duplicate
 * (`apps/web/.../get-current-user.ts` and `apps/mobile/.../get-auth.ts` both hand-flatten the raw
 * `{ user }` response — which embeds the full `ownedInstitution`/`institutionMember` relations,
 * not the slim client-facing shape — into `User`): `transformResponse` does that reshape once
 * here instead of twice per app.
 */
export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    signUp: builder.mutation<AuthResponse, SignUpPayload>({
      query: (body) => ({ path: ['auth', 'sign-up'], method: 'post', body }),
    }),

    signIn: builder.mutation<MfaRequiredResponse, SignInPayload>({
      query: (body) => ({ path: ['auth', 'sign-in'], method: 'post', body }),
    }),

    verifyOtp: builder.mutation<AuthResponse, VerifyOtpPayload>({
      query: (body) => ({ path: ['auth', 'verify-otp'], method: 'post', body }),
    }),

    resendOtp: builder.mutation<void, ResendOtpPayload>({
      query: (body) => ({ path: ['auth', 'resend-otp'], method: 'post', body }),
      transformResponse: (): void => undefined,
    }),

    logout: builder.mutation<void, RefreshTokenPayload>({
      query: (body) => ({ path: ['auth', 'logout'], method: 'post', body }),
      transformResponse: (): void => undefined,
    }),

    forgotPassword: builder.mutation<void, ForgotPasswordPayload>({
      query: (body) => ({ path: ['auth', 'forgot-password'], method: 'post', body }),
      transformResponse: (): void => undefined,
    }),

    resetPassword: builder.mutation<void, ResetPasswordPayload>({
      query: (body) => ({ path: ['auth', 'reset-password'], method: 'post', body }),
      transformResponse: (): void => undefined,
    }),

    getCurrentUser: builder.mutation<User, void>({
      query: () => ({ path: ['auth'], method: 'get' }),
      transformResponse: (response: RawCurrentUserResponse) => toUser(response.user),
    }),

    changePassword: builder.mutation<void, ChangePasswordPayload>({
      query: (body) => ({ path: ['auth', 'change-password'], method: 'put', body }),
      transformResponse: (): void => undefined,
    }),

    changeEmail: builder.mutation<void, ChangeEmailPayload>({
      query: (body) => ({ path: ['auth', 'change-email'], method: 'put', body }),
      transformResponse: (): void => undefined,
    }),

    verifyEmailChange: builder.mutation<User, VerifyEmailChangePayload>({
      query: (body) => ({ path: ['auth', 'verify-email-change'], method: 'post', body }),
      transformResponse: (response: VerifyEmailChangeResponse) => response.user,
    }),

    deleteAccount: builder.mutation<void, DeleteAccountPayload>({
      query: (body) => ({ path: ['auth', 'delete-account'], method: 'post', body }),
      transformResponse: (): void => undefined,
    }),

    cancelDeleteAccount: builder.mutation<void, void>({
      query: () => ({ path: ['auth', 'cancel-delete-account'], method: 'delete' }),
      transformResponse: (): void => undefined,
    }),
  }),
});

export const {
  useSignUpMutation,
  useSignInMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
  useLogoutMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetCurrentUserMutation,
  useChangePasswordMutation,
  useChangeEmailMutation,
  useVerifyEmailChangeMutation,
  useDeleteAccountMutation,
  useCancelDeleteAccountMutation,
} = authApi;
